import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Activity } from "lucide-react";

const BAND_STYLE = {
  red: "bg-[#DC2626] text-white",
  orange: "bg-[#F97316] text-white",
  yellow: "bg-[#EAB308] text-slate-900",
  green: "bg-[#16A34A] text-white",
};

export default function RiskAssessment({ caseId, onDone }) {
  const [tpl, setTpl] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const loadHistory = async () => {
    try { const { data } = await api.get(`/cases/${caseId}/assessments`); setHistory(data); }
    catch (e) { console.warn("assessment history fetch failed", e); }
  };
  useEffect(() => { loadHistory(); }, [caseId]);

  const openModal = async () => {
    setErr(""); setResult(null); setAnswers({});
    try {
      const { data } = await api.get("/assessment-templates/active");
      setTpl(data);
      setOpen(true);
    } catch (e) { setErr("No active assessment template found. Admin must create one."); setOpen(true); }
  };

  const submit = async () => {
    setSaving(true); setErr("");
    try {
      const { data } = await api.post(`/cases/${caseId}/assessment`, { template_id: tpl.id, answers });
      setResult(data);
      loadHistory();
      onDone?.();
    } catch (e) { setErr(e.response?.data?.detail?.toString() || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-8 border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-[#0033A0]" />
          <div className="overline">National Child Risk Assessment</div>
        </div>
        <button data-testid="run-assessment" onClick={openModal} className="border border-[#0033A0] text-[#0033A0] px-4 py-2 text-xs hover:bg-[#0033A0] hover:text-white">
          Run assessment
        </button>
      </div>

      {history.length === 0 && <div className="text-sm text-slate-500">No assessments recorded yet.</div>}
      <ul className="space-y-2">
        {history.map(h => (
          <li key={h.id} className="flex items-center justify-between text-sm border border-slate-100 p-3">
            <div>
              <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${BAND_STYLE[h.band]}`}>{h.band}</span>
              <span className="ml-3 font-mono text-xs">score {h.score}</span>
              <span className="ml-3 text-slate-500 text-xs">{h.template_name}</span>
            </div>
            <div className="text-xs text-slate-500 font-mono">{h.assessed_by} · {h.assessed_at?.slice(0,16).replace("T"," ")}</div>
          </li>
        ))}
      </ul>

      {open && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl border border-slate-300 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <div className="overline">Triage</div>
                <h3 className="font-heading font-extrabold text-2xl">{tpl?.name || "Assessment"}</h3>
                {tpl?.description && <p className="text-xs text-slate-500 mt-1">{tpl.description}</p>}
              </div>
              <button onClick={() => setOpen(false)}><X /></button>
            </div>
            <div className="p-6 space-y-5">
              {err && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{err}</div>}
              {result && (
                <div className="border border-slate-200 p-4">
                  <div className="overline">Result</div>
                  <div className="flex items-center gap-4 mt-2">
                    <span className={`px-3 py-2 text-sm font-bold uppercase tracking-wider ${BAND_STYLE[result.band]}`}>{result.band}</span>
                    <span className="font-mono text-2xl font-heading font-extrabold">{result.score}</span>
                    <span className="text-slate-500 text-xs">/ 100</span>
                  </div>
                </div>
              )}
              {!result && tpl?.questions?.map((q, idx) => (
                <div key={q.id} className="border-l-2 border-slate-200 pl-4">
                  <label className="block text-sm font-semibold mb-2">{idx+1}. {q.text}</label>
                  {q.type === "yes_no" && (
                    <div className="flex gap-2">
                      {["yes","no"].map(v => (
                        <button key={v} type="button" onClick={() => setAnswers({...answers, [q.id]: v})}
                          className={`px-4 py-2 border text-xs uppercase tracking-wider ${answers[q.id]===v ? "bg-[#0033A0] text-white border-[#0033A0]" : "border-slate-300 hover:bg-slate-100"}`}>
                          {v}
                        </button>
                      ))}
                    </div>
                  )}
                  {q.type === "scale" && (
                    <div className="flex gap-2">
                      {[0,1,2,3,4,5].map(n => (
                        <button key={n} type="button" onClick={() => setAnswers({...answers, [q.id]: n})}
                          className={`w-10 h-10 border text-sm font-mono ${answers[q.id]===n ? "bg-[#0033A0] text-white border-[#0033A0]" : "border-slate-300 hover:bg-slate-100"}`}>
                          {n}
                        </button>
                      ))}
                    </div>
                  )}
                  {q.type === "choice" && (
                    <div className="flex gap-2 flex-wrap">
                      {q.options?.map(o => (
                        <button key={o.value} type="button" onClick={() => setAnswers({...answers, [q.id]: o.value})}
                          className={`px-3 py-2 border text-xs ${answers[q.id]===o.value ? "bg-[#0033A0] text-white border-[#0033A0]" : "border-slate-300 hover:bg-slate-100"}`}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-200 p-4">
              <button onClick={() => setOpen(false)} className="border border-slate-300 px-5 py-3">Close</button>
              {!result && <button data-testid="submit-assessment" onClick={submit} disabled={saving || !tpl} className="bg-[#0033A0] text-white px-5 py-3 hover:bg-slate-900 disabled:opacity-60">{saving ? "Scoring…" : "Compute risk"}</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
